import { forwardRef } from 'react';

import { cva } from 'cva';
import type { ModalOverlayProps } from 'react-aria-components/Modal';
import { ModalOverlay, Modal as RACModal } from 'react-aria-components/Modal';

import animationStyles from '../../styles/animation.module.css';
import styles from './modal.module.css';

const modalOverlayVariants = cva({
  base: styles.modal_overlay,
  variants: {
    isEntering: {
      true: animationStyles.animate_enter,
    },
    isExiting: {
      true: animationStyles.animate_exit,
    },
  },
});

const modalVariants = cva({
  base: styles.modal,
  variants: {
    isEntering: {
      true: animationStyles.animate_enter,
    },
    isExiting: {
      true: animationStyles.animate_exit,
    },
  },
});

/**
 * Modal component.
 *
 * `Modal` - An overlay element which blocks interaction with elements outside it. Render Dialog within a Modal to make it a modal dialog.
 */

export type ModalProps = ModalOverlayProps & {
  isDismissible?: boolean;
  overlayClassName?: string;
};

export const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      className,
      overlayClassName,
      isDismissible,
      children,
      ...props
    }: ModalProps,
    ref,
  ) => {
    return (
      <ModalOverlay
        {...props}
        className={({ isEntering, isExiting }) =>
          modalOverlayVariants({
            isEntering,
            isExiting,
            className: overlayClassName,
          })
        }
      >
        <RACModal
          ref={ref}
          className={({ isEntering, isExiting }) =>
            modalVariants({ isEntering, isExiting, className })
          }
          isDismissable={isDismissible}
        >
          {children}
        </RACModal>
      </ModalOverlay>
    );
  },
);
